import UserDTO from "@/dto/UserDTO";
import {NODES_BY_ROLES, Role, ROLES_BY_CODES} from "@/constants/Role";

type GrantDTO = {
    i: number;   // grant id
    d: string;   // domain
    a: string;   // action
    c: number;   // companyId
    p?: number | null; // projectId
    e?: number | null; // employmentId
    h?: number;  // holderId
    pk?: string; // principalKind
    pid?: number;// principalId
    aa?: string | null; // assignedAt (ISO)
    x?: string | null;  // revokedAt/expiry (ISO)
};

type SecondaryPerms = { roles: Array<Role>; g?: Array<GrantDTO> };

class Auth {

    private user: UserDTO | null = this.initUser();
    private roles: Array<Role> = this.initRoles();
    private grants: Array<GrantDTO> = this.initGrants();
    private secondaryToken: string | null = this.initSecondaryToken();

    private initUser(): UserDTO | null {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : null;
    }

    private initRoles(): Array<Role> {
        const storedRoles = localStorage.getItem("roles");
        if(storedRoles){
            const parsedHashCodes: Array<number> = JSON.parse(storedRoles)
            return parsedHashCodes.map(hash => ROLES_BY_CODES.get(hash)).filter(r => r != undefined) as Array<Role>;
        }else {
            return [];
        }
    }

    private initGrants(): Array<GrantDTO> {
        const stored = localStorage.getItem("grants");
        if (!stored) return [];
        try {
            return JSON.parse(stored) as Array<GrantDTO>;
        } catch {
            return [];
        }
    }

    private initSecondaryToken(): string | null {
        return localStorage.getItem("secondaryToken");
    }

    get getUser(): UserDTO | null {
        return this.user;
    }

    get getSecondaryToken(): string | null {
        return this.secondaryToken;
    }

    setUser(user: UserDTO | null) {
        this.user = user;
        localStorage.setItem('user', JSON.stringify(user));
    }

    setPerms(secondaryToken: string) {
        // Check if token has actually changed
        if (this.secondaryToken === secondaryToken) {
            return;
        }

        let data: SecondaryPerms;
        try {
            data = JSON.parse(atob(secondaryToken));
        } catch {
            localStorage.removeItem("roles");
            localStorage.removeItem("grants");
            localStorage.removeItem("secondaryToken");
            this.roles = [];
            this.grants = [];
            this.secondaryToken = null;
            return;
        }

        // Store the token first
        this.secondaryToken = secondaryToken;
        localStorage.setItem("secondaryToken", secondaryToken);

        // Then update roles and grants
        localStorage.setItem("roles", JSON.stringify(data.roles.map((r) => NODES_BY_ROLES.get(r)?.hashCode))
        );
        this.roles = this.initRoles();

        localStorage.setItem("grants", JSON.stringify(data.g));
        this.grants = this.initGrants();

        // Dispatch a custom event to notify watchers
        window.dispatchEvent(new Event('secondaryTokenChanged'));
    }

    hasPermission(role: Role): boolean{
        return !!this.roles.find(r => NODES_BY_ROLES.get(r)?.isOrHasInTree(role));
    }

    hasGrant(
        domain: string,
        action: string,
        scope?: { companyId?: number; projectId?: number | null; employmentId?: number | null }
    ): boolean {
        const d = domain.toUpperCase();
        const a = action.toUpperCase();
        const cId = scope?.companyId ?? null;
        const pId = scope?.projectId ?? null;
        const eId = scope?.employmentId ?? null;

        const matches = this.grants.filter((g) => g.d === d && g.a === a && (cId == null || g.c === cId));
        if (matches.length === 0) return false;

        // company-level covers everything within company
        const hasCompanyLevel = matches.some((g) => g.p == null && g.e == null);
        if (hasCompanyLevel) return true;

        if(eId != null && pId != null){
            // direct grant on employment within project
            return matches.some((g) => g.e === eId && g.p === pId);
        }else if (eId != null) {
            // direct grant on employment
            return matches.some((g) => g.e === eId);
        } else if (pId != null) {
            // direct grant on project
            return matches.some((g) => g.p === pId && g.e == null);
        }

        return false;
    }

    hasDomainAccess(domain: string): boolean {
        return this.grants.map(g => g.d.toUpperCase().trim()).includes(domain.toUpperCase().trim());
    }

    clear(){
        this.user = null;
        this.roles = [];
        this.grants = [];
        this.secondaryToken = null;
        localStorage.removeItem("user");
        localStorage.removeItem("roles");
        localStorage.removeItem("grants");
        localStorage.removeItem("secondaryToken");
    }

}

export default new Auth();

